import { createNumberColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/NumberColumn.tsx';
import { createTextColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/TextColumn.tsx';
import {
	createSingleLineHeader,
	SINGLE_LINE_STYLE,
} from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/singleLine.ts';
import { ExtendedColumnDef } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting.ts';
import { AmountFormatted } from '@dealstack/frontend/shared/components/AmountFormatted/AmountFormatted.tsx';
import { LONG_DASH } from '@dealstack/frontend/shared/other/react/specialSigns.tsx';
import { Button, Flex, Text, Tooltip } from '@mantine/core';
import { InvestmentDetailsAllocation, SecurityType } from '@dealstack/shared/lib/generated';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types.ts';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n.ts';
import { formatNumber } from '@dealstack/shared/lib/format/number';

interface Params {
	tMipManage: TranslateFunction<I18Namespaces.MIP_MANAGE>;
	getSecurityTypeTranslation: (type: SecurityType) => string;
	canMutateAllocations: boolean;
	onEditAllocation?: (allocation: InvestmentDetailsAllocation) => void;
}

export const getInvestmentAllocationsTableColumns = ({
	tMipManage,
	getSecurityTypeTranslation,
	canMutateAllocations,
	onEditAllocation,
}: Params): ExtendedColumnDef<InvestmentDetailsAllocation>[] => [
	createTextColumn<InvestmentDetailsAllocation>({
		id: 'securityName',
		accessorKey: 'securityName',
		header: createSingleLineHeader<InvestmentDetailsAllocation>(
			tMipManage('investment_allocations_table.columns.security_name')
		),
		size: 180,
		minSize: 140,
		columnLabel: tMipManage('investment_allocations_table.columns.security_name'),
		enableSorting: true,
	}),

	createTextColumn<InvestmentDetailsAllocation>({
		id: 'securityType',
		header: createSingleLineHeader<InvestmentDetailsAllocation>(
			tMipManage('investment_allocations_table.columns.security_type')
		),
		size: 150,
		minSize: 140,
		columnLabel: tMipManage('investment_allocations_table.columns.security_type'),
		accessorFn: (row) => getSecurityTypeTranslation(row.securityType),
		enableSorting: true,
	}),

	createTextColumn<InvestmentDetailsAllocation>({
		id: 'issuingEntityName',
		accessorKey: 'issuingEntityName',
		header: createSingleLineHeader<InvestmentDetailsAllocation>(
			tMipManage('investment_allocations_table.columns.issuing_entity')
		),
		size: 180,
		minSize: 140,
		columnLabel: tMipManage('investment_allocations_table.columns.issuing_entity'),
		enableSorting: true,
	}),

	createTextColumn<InvestmentDetailsAllocation>({
		id: 'vestingTermName',
		header: createSingleLineHeader<InvestmentDetailsAllocation>(
			tMipManage('investment_allocations_table.columns.vesting_term')
		),
		size: 180,
		minSize: 140,
		columnLabel: tMipManage('investment_allocations_table.columns.vesting_term'),
		accessorFn: (row) => row.vestingTermName ?? LONG_DASH,
		enableSorting: true,
	}),

	createTextColumn<InvestmentDetailsAllocation>({
		id: 'transactionType',
		header: createSingleLineHeader<InvestmentDetailsAllocation>(
			tMipManage('investment_allocations_table.columns.transaction_type')
		),
		size: 150,
		minSize: 150,
		columnLabel: tMipManage('investment_allocations_table.columns.transaction_type'),
		accessorFn: (row) => row.transactionType ?? LONG_DASH,
		enableSorting: true,
	}),

	createNumberColumn<InvestmentDetailsAllocation>({
		id: 'units',
		accessorKey: 'units',
		header: createSingleLineHeader<InvestmentDetailsAllocation>(
			tMipManage('investment_allocations_table.columns.units'),
			{ align: 'right' }
		),
		size: 90,
		minSize: 90,
		columnLabel: tMipManage('investment_allocations_table.columns.units'),
		enableSorting: true,
		cell: ({ row }) => (
			<Flex justify="flex-end" w="100%">
				<Text style={SINGLE_LINE_STYLE}>{formatNumber({ value: row.original.units })}</Text>
			</Flex>
		),
	}),

	createNumberColumn<InvestmentDetailsAllocation>({
		id: 'cost',
		accessorKey: 'cost',
		header: createSingleLineHeader<InvestmentDetailsAllocation>(
			tMipManage('investment_allocations_table.columns.total_cost'),
			{ align: 'right' }
		),
		size: 130,
		minSize: 130,
		columnLabel: tMipManage('investment_allocations_table.columns.total_cost'),
		enableSorting: true,
		cell: ({ row }) => (
			<Flex justify="flex-end" w="100%">
				<Text style={SINGLE_LINE_STYLE}>
					<AmountFormatted
						value={row.original.cost}
						currency={row.original.securityCurrency}
						showCurrency
					/>
				</Text>
			</Flex>
		),
	}),

	createNumberColumn<InvestmentDetailsAllocation>({
		id: 'actions',
		accessorKey: 'actions',
		header: () => null,
		columnLabel: tMipManage('investment_allocations_table.columns.manage'),
		enableSorting: false,
		size: 100,
		minSize: 100,
		maxSize: 100,
		cell: ({ row }) => {
			const editAllocationButton = (
				<Button
					variant="subtle"
					onClick={() => onEditAllocation?.(row.original)}
					disabled={!canMutateAllocations}
				>
					<Text c="dark.8" style={SINGLE_LINE_STYLE}>
						{tMipManage('investment_allocations_table.columns.manage')}
					</Text>
				</Button>
			);

			return (
				<Flex justify="flex-end" w="100%">
					{canMutateAllocations ? (
						editAllocationButton
					) : (
						<Tooltip label={tMipManage('investment_allocations.frozen_tooltip')}>
							<div>{editAllocationButton}</div>
						</Tooltip>
					)}
				</Flex>
			);
		},
	}),
];
